import {
  AIAgent,
  AgentRole,
  AgentInfoCategory,
  AgentGoalData,
  AgentToolInfo,
  AgentKnowledgeItem,
  AgentModelConfig,
  AgentCostMetrics,
  AgentContextSnapshot,
  AgentDecision,
  AgentDependency,
} from '../types';

export interface FullAgentInfo {
  task: {
    title: string;
    description: string;
    progress: number;
    complexity: 'easy' | 'medium' | 'hard';
    status: string;
    startedAtFormatted: string;
    etaFormatted: string;
    queueCount: number;
    queuedTasks: { id: string; title: string; complexity: string; priority: string }[];
    outputPreview?: string;
  };
  goal: AgentGoalData;
  context: AgentContextSnapshot;
  tools: AgentToolInfo[];
  knowledge: AgentKnowledgeItem[];
  model: AgentModelConfig;
  cost: AgentCostMetrics;
  state: {
    status: string;
    statusLabel: string;
    statusDurationSec: number;
    energy: number;
    bladder: number;
    focus: number;
    currentLocationName: string;
    processedTasksCount: number;
    avgResponseTimeSec: number;
    thoughts: { time: string; text: string; mood: 'happy' | 'tired' | 'busy' | 'break' }[];
  };
  decisions: AgentDecision[];
  dependencies: AgentDependency[];
}

export function getAgentFullDetails(
  agent: AIAgent,
  allAgents: AIAgent[] = []
): FullAgentInfo {
  const role = agent.role || 'backend';

  // 1. TASK
  const currentTask = agent.currentTask || (agent.taskQueue && agent.taskQueue[0]);
  const taskTitle =
    currentTask?.title ||
    (agent.status === 'working'
      ? getRoleDefaultActiveTask(role)
      : 'Waiting for new tasks in queue');
  const taskProgress =
    typeof currentTask?.progress === 'number'
      ? currentTask.progress
      : agent.status === 'working'
      ? Math.min(95, Math.max(15, Math.round((agent.statusTimer / 45) * 100)))
      : 0;

  const queuedTasks = (agent.taskQueue || []).map((t, idx) => ({
    id: t.id || `task_${idx}`,
    title: t.title || `Subtask #${idx + 1}`,
    complexity: t.complexity || 'medium',
    priority: idx === 0 ? 'High' : 'Normal',
  }));

  if (queuedTasks.length === 0 && agent.status === 'working') {
    queuedTasks.push(
      { id: 'q1', title: 'Execute regression and coverage test suite', complexity: 'medium', priority: 'Normal' },
      { id: 'q2', title: 'Generate telemetry reports and build artifacts', complexity: 'easy', priority: 'Low' }
    );
  }

  const taskInfo = {
    title: taskTitle,
    description:
      currentTask?.contextData ||
      `Active pipeline processing for ${role.toUpperCase()} role. Autonomous execution with type validation and security policies.`,
    progress: taskProgress,
    complexity: (currentTask?.complexity || 'medium') as 'easy' | 'medium' | 'hard',
    status:
      agent.status === 'working'
        ? 'In Progress (Executing)'
        : agent.status === 'coffee_break'
        ? 'Paused (Cafeteria)'
        : agent.status === 'bathroom_break'
        ? 'Paused (Restroom)'
        : 'Standby / Awaiting Trigger',
    startedAtFormatted: '3 min 42 sec ago',
    etaFormatted: agent.status === 'working' ? '45 sec remaining' : 'N/A',
    queueCount: agent.taskQueue?.length || queuedTasks.length,
    queuedTasks,
    outputPreview: agent.lastOutput || 'Waiting for current step completion to emit structured log...',
  };

  // 2. GOAL
  const goal: AgentGoalData = agent.goalData || getRoleGoalData(role, agent.name);

  // 3. CONTEXT
  const context: AgentContextSnapshot = agent.contextSnapshot || {
    activeSessionTokens: 18450,
    maxContextTokens: 1048576,
    memoryItemsCount: 42,
    ragChunksLoaded: 8,
    systemPromptSummary:
      agent.systemPrompt ||
      `You are an autonomous ${role.toUpperCase()} agent specializing in scalable architecture, resilience, and continuous delivery.`,
    environmentVariables: {
      NODE_ENV: 'production',
      AGENT_ROLE: role,
      EXECUTION_MODE: 'autonomous_pipeline',
      MAX_RETRIES: '3',
      LOG_LEVEL: 'debug',
      CLUSTER_REGION: 'europe-west1',
    },
    lastPromptInjected: `[SYSTEM CONTEXT]: You are ${agent.name} (${role}). Process the task respecting SLA < 2.5s without side effects.`,
  };

  // 4. TOOLS
  const tools: AgentToolInfo[] =
    agent.tools && agent.tools.length > 0 ? agent.tools : getRoleTools(role);

  // 5. KNOWLEDGE
  const knowledge: AgentKnowledgeItem[] =
    agent.knowledge && agent.knowledge.length > 0 ? agent.knowledge : getRoleKnowledge(role);

  // 6. MODEL
  const model: AgentModelConfig = agent.modelConfig || {
    provider: 'Google DeepMind',
    modelName: role === 'devops' || role === 'backend' ? 'Gemini 2.5 Pro (Thinking)' : 'Gemini 2.5 Flash',
    temperature: 0.2,
    topP: 0.95,
    maxOutputTokens: 8192,
    contextWindowTokens: 1048576,
    avgInferenceLatencyMs: role === 'devops' || role === 'backend' ? 240 : 110,
  };

  // 7. COST
  const cost: AgentCostMetrics = agent.costMetrics || {
    totalInputTokens: 142500 + agent.processedTasksCount * 1250,
    totalOutputTokens: 38400 + agent.processedTasksCount * 420,
    totalCostUsd: Number((0.024 + agent.processedTasksCount * 0.0018).toFixed(4)),
    avgTokensPerSec: 185.4,
    requestsCount: 28 + agent.processedTasksCount,
    currency: 'USD',
  };

  // 8. STATE
  const state = {
    status: agent.status,
    statusLabel: getStatusLabel(agent.status),
    statusDurationSec: agent.statusTimer || 12,
    energy: Math.round(agent.energy),
    bladder: Math.round(agent.bladder),
    focus: Math.round(agent.focus),
    currentLocationName: `Grid (${agent.currentCol}, ${agent.currentRow})`,
    processedTasksCount: agent.processedTasksCount || 0,
    avgResponseTimeSec: agent.avgResponseTimeSec || 1.8,
    thoughts: agent.thoughts || [
      { time: 'Now', text: 'Analyzing execution flow and system metrics.', mood: 'happy' as const },
    ],
  };

  // 9. DECISIONS
  const decisions: AgentDecision[] =
    agent.decisions && agent.decisions.length > 0
      ? agent.decisions
      : getRoleDecisions(role, agent.name);

  // 10. DEPENDENCIES
  const dependencies: AgentDependency[] =
    agent.dependencies && agent.dependencies.length > 0
      ? agent.dependencies
      : getRoleDependencies(role, allAgents);

  return {
    task: taskInfo,
    goal,
    context,
    tools,
    knowledge,
    model,
    cost,
    state,
    decisions,
    dependencies,
  };
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'working':
      return 'Working / Processing';
    case 'coffee_break':
      return 'Cafeteria Break';
    case 'bathroom_break':
      return 'Restroom Break';
    case 'meeting':
      return 'In Alignment Meeting';
    case 'blocked':
      return 'Blocked (Awaiting Dependency)';
    case 'idle':
      return 'Available / Standby';
    case 'walking':
      return 'In Transit / Moving';
    default:
      return status;
  }
}

function getRoleDefaultActiveTask(role: AgentRole): string {
  switch (role) {
    case 'devops':
      return 'Sync Kubernetes manifests and audit cluster pods';
    case 'frontend':
      return 'Optimize Webpack bundle and reactive Tailwind components';
    case 'backend':
      return 'Implement GraphQL endpoints and ACID transaction migrations';
    case 'qa':
      return 'Run Playwright E2E suite and regression testing';
    case 'data':
      return 'Analytics aggregation pipeline and Parquet index optimization';
    case 'marketing':
      return 'Generate copy and promotional creative assets with multimodal model';
    case 'support':
      return 'Automated ticket categorization and resolution with RAG';
    case 'manager':
      return 'Sprint planning, load balancing, and SLA monitoring';
    default:
      return 'Executing workflow task';
  }
}

function getRoleGoalData(role: AgentRole, agentName: string): AgentGoalData {
  switch (role) {
    case 'devops':
      return {
        primaryGoal: 'Ensure 99.99% uptime and latency < 50ms across CI/CD pipelines.',
        strategicPurpose:
          'Supervise cloud infrastructure, orchestrate containers, and resolve deployment incidents in real time.',
        targetKpis: ['Uptime >= 99.99%', 'MTTR < 4 min', 'Build Time < 90s'],
        milestones: [
          { id: 'm1', title: 'Canary release v2.8 deployment', completed: true, progressPct: 100 },
          { id: 'm2', title: 'Configure horizontal pod autoscaling (HPA)', completed: false, progressPct: 75 },
          { id: 'm3', title: 'VPC security and mTLS audit', completed: false, progressPct: 30 },
        ],
      };
    case 'frontend':
      return {
        primaryGoal: 'Deliver ultra-smooth 60 FPS web experiences with AA accessibility.',
        strategicPurpose:
          'Build visual components, design isometric layouts, and optimize real-time interactions.',
        targetKpis: ['LCP < 1.2s', 'CLS < 0.05', 'FID < 50ms', 'Accessibility 100/100'],
        milestones: [
          { id: 'm1', title: 'Design system refactor', completed: true, progressPct: 100 },
          { id: 'm2', title: '16-frame sprite engine integration', completed: true, progressPct: 100 },
          { id: 'm3', title: 'Multi-touch and keyboard shortcut support', completed: false, progressPct: 60 },
        ],
      };
    case 'backend':
      return {
        primaryGoal: 'Model high-concurrency APIs with safe transactions and zero deadlocks.',
        strategicPurpose:
          'Develop business logic, govern persistence models, and process distributed event queues.',
        targetKpis: ['Throughput > 5,000 req/s', 'Error Rate < 0.01%', 'Database Latency < 5ms'],
        milestones: [
          { id: 'm1', title: 'Migration to partitioned PostgreSQL 16', completed: true, progressPct: 100 },
          { id: 'm2', title: 'Distributed cache with Redis Cluster', completed: false, progressPct: 80 },
          { id: 'm3', title: 'Inter-service gRPC implementation', completed: false, progressPct: 40 },
        ],
      };
    case 'qa':
      return {
        primaryGoal: 'Zero bugs in production via comprehensive test coverage.',
        strategicPurpose:
          'Design automated test plans, simulate edge scenarios, and validate interface contracts.',
        targetKpis: ['Code Coverage > 90%', 'Regression Detection 100%', 'Flaky Tests 0%'],
        milestones: [
          { id: 'm1', title: 'Playwright E2E test suite', completed: true, progressPct: 100 },
          { id: 'm2', title: 'k6 load testing (10k virtual users)', completed: false, progressPct: 85 },
          { id: 'm3', title: 'OpenAPI contract validation with Prism', completed: false, progressPct: 50 },
        ],
      };
    default:
      return {
        primaryGoal: `Deliver operational excellence for goals assigned to the ${role} role.`,
        strategicPurpose: `Align technical execution for ${agentName} with organizational priorities.`,
        targetKpis: ['Response SLA < 2s', 'Delivery Quality 98%', 'Active Availability'],
        milestones: [
          { id: 'm1', title: 'Initialization and diagnostic phase', completed: true, progressPct: 100 },
          { id: 'm2', title: 'Primary pipeline execution', completed: false, progressPct: 65 },
          { id: 'm3', title: 'Review and results consolidation', completed: false, progressPct: 20 },
        ],
      };
  }
}

function getRoleTools(role: AgentRole): AgentToolInfo[] {
  switch (role) {
    case 'devops':
      return [
        {
          id: 'tool_k8s',
          name: 'mcp://k8s-cluster-gateway',
          type: 'mcp',
          status: 'active',
          description: 'Manage pods, deployments, replicas, and namespaces in Kubernetes cluster.',
          endpointOrSchema: 'kubectl get/apply/rollout/logs',
          latencyMs: 14,
          callsCount: 142,
        },
        {
          id: 'tool_terraform',
          name: 'terraform_cloud_api',
          type: 'api',
          status: 'active',
          description: 'Declarative cloud infrastructure provisioning on GCP.',
          endpointOrSchema: 'POST /v2/runs/apply',
          latencyMs: 85,
          callsCount: 19,
        },
        {
          id: 'tool_datadog',
          name: 'datadog_metrics_stream',
          type: 'api',
          status: 'active',
          description: 'Read APM metrics, p99 latency, and CPU saturation alerts.',
          endpointOrSchema: 'GET /api/v1/query',
          latencyMs: 32,
          callsCount: 512,
        },
      ];
    case 'frontend':
      return [
        {
          id: 'tool_vite',
          name: 'mcp://vite-dev-server',
          type: 'mcp',
          status: 'active',
          description: 'HMR compiler and bundler for React + Tailwind modules.',
          endpointOrSchema: 'build/lint/bundle',
          latencyMs: 8,
          callsCount: 380,
        },
        {
          id: 'tool_figma',
          name: 'figma_rest_tokens',
          type: 'api',
          status: 'active',
          description: 'Extraction of design tokens, color palettes, and UI spacing.',
          endpointOrSchema: 'GET /v1/files/:key/variables',
          latencyMs: 45,
          callsCount: 64,
        },
        {
          id: 'tool_lighthouse',
          name: 'lighthouse_ci_runner',
          type: 'cli',
          status: 'idle',
          description: 'Audit Core Web Vitals, accessibility, and SEO.',
          endpointOrSchema: 'lhci autorun',
          latencyMs: 1200,
          callsCount: 12,
        },
      ];
    case 'backend':
      return [
        {
          id: 'tool_postgres',
          name: 'mcp://postgres-gateway.internal:8080',
          type: 'mcp',
          status: 'active',
          description: 'Execution of parameterized SQL queries, connection pooling, and migrations.',
          endpointOrSchema: 'execute_sql / transaction_pool',
          latencyMs: 6,
          callsCount: 890,
        },
        {
          id: 'tool_redis',
          name: 'redis_cluster_client',
          type: 'database',
          status: 'active',
          description: 'In-memory key-value store for session caching and rate-limiting.',
          endpointOrSchema: 'GET/SET/EXPIRE/PUB_SUB',
          latencyMs: 1.2,
          callsCount: 2400,
        },
        {
          id: 'tool_graphql',
          name: 'graphql_schema_builder',
          type: 'cli',
          status: 'active',
          description: 'Generation of typed schemas and asynchronous resolvers.',
          endpointOrSchema: 'codegen --watch',
          latencyMs: 18,
          callsCount: 155,
        },
      ];
    case 'qa':
      return [
        {
          id: 'tool_playwright',
          name: 'mcp://playwright-test-runner',
          type: 'mcp',
          status: 'active',
          description: 'Headless browser automation on Chromium, Firefox, and WebKit.',
          endpointOrSchema: 'npx playwright test --reporter=json',
          latencyMs: 450,
          callsCount: 88,
        },
        {
          id: 'tool_jest',
          name: 'vitest_unit_engine',
          type: 'cli',
          status: 'active',
          description: 'Unit test executor and code coverage analysis.',
          endpointOrSchema: 'vitest run --coverage',
          latencyMs: 95,
          callsCount: 310,
        },
      ];
    default:
      return [
        {
          id: 'tool_general',
          name: 'mcp://universal-workspace-agent',
          type: 'mcp',
          status: 'active',
          description: 'Context processing and task bus integration tool.',
          endpointOrSchema: 'run_task_action',
          latencyMs: 20,
          callsCount: 100,
        },
      ];
  }
}

function getRoleKnowledge(role: AgentRole): AgentKnowledgeItem[] {
  switch (role) {
    case 'devops':
      return [
        {
          id: 'kn_k8s',
          name: 'Kubernetes Production Runbooks v1.31',
          type: 'playbook',
          tokensCount: 48000,
          lastUpdated: 'Today 04:15',
          description: 'Canary deployment guidelines, network policies, and secret management.',
          relevanceScore: 0.98,
        },
        {
          id: 'kn_infra',
          name: 'GCP Cloud Architecture Reference Guide',
          type: 'documentation',
          tokensCount: 92000,
          lastUpdated: 'Yesterday 18:20',
          description: 'Specification of VPC topology, Cloud Run, IAM, and Cloud Armor.',
          relevanceScore: 0.94,
        },
      ];
    case 'frontend':
      return [
        {
          id: 'kn_ui',
          name: 'IsoDesign Design System & Tailwind Guidelines',
          type: 'documentation',
          tokensCount: 34000,
          lastUpdated: 'Today 09:00',
          description: 'Spacing rules, typographic hierarchy, contrast ratios, and Motion animations.',
          relevanceScore: 0.99,
        },
        {
          id: 'kn_perf',
          name: 'Web Performance Optimization Bible 2026',
          type: 'documentation',
          tokensCount: 56000,
          lastUpdated: '3 days ago',
          description: 'Isometric canvas rendering techniques, virtualization, and sprite caching.',
          relevanceScore: 0.92,
        },
      ];
    case 'backend':
      return [
        {
          id: 'kn_db',
          name: 'PostgreSQL 16 High-Concurrency Tuning',
          type: 'documentation',
          tokensCount: 78000,
          lastUpdated: 'Today 06:30',
          description: 'Manual for ACID transaction optimization, BRIN/GIN indexes, and SERIALIZABLE isolation.',
          relevanceScore: 0.97,
        },
        {
          id: 'kn_api',
          name: 'Enterprise Microservices OpenAPI 3.1 Contract',
          type: 'api_spec',
          tokensCount: 115000,
          lastUpdated: 'Today 11:15',
          description: 'REST endpoint definitions, payload schemas, and standardized error codes.',
          relevanceScore: 0.99,
        },
      ];
    case 'qa':
      return [
        {
          id: 'kn_qa',
          name: 'Test Automation Strategy & Edge-Case Catalog',
          type: 'playbook',
          tokensCount: 42000,
          lastUpdated: 'Today 08:45',
          description: 'Catalog for load testing, visual regression, and network fault injection.',
          relevanceScore: 0.96,
        },
      ];
    default:
      return [
        {
          id: 'kn_base',
          name: 'Operations & Best Practices Handbook',
          type: 'documentation',
          tokensCount: 35000,
          lastUpdated: 'Yesterday',
          description: 'General workflow guidelines and agent collaboration policies.',
          relevanceScore: 0.9,
        },
      ];
  }
}

function getRoleDecisions(role: AgentRole, agentName: string): AgentDecision[] {
  return [
    {
      id: 'dec_1',
      timestamp: Date.now() - 180000,
      clockTime: '14:28:15',
      trigger: 'High incoming task queue load detected',
      reasoning:
        'Request volume surged by 40%. To avoid SLA degradation, concurrent batch execution was prioritized.',
      chosenAction: 'Activate concurrent worker pool and parallelize processing.',
      confidencePct: 96,
      outcome: 'Average latency stabilized at 1.8 seconds.',
      mcpToolUsed: 'universal-workspace-agent',
    },
    {
      id: 'dec_2',
      timestamp: Date.now() - 90000,
      clockTime: '14:31:02',
      trigger: 'Memory and resource constraints verification',
      reasoning:
        'Memory usage reached 72%. Executed preventive garbage collection and local cache compaction.',
      chosenAction: 'Flush temporary buffers and resume active task.',
      confidencePct: 99,
      outcome: 'Memory reduced to 38% without disrupting active workflow.',
      mcpToolUsed: 'redis_cluster_client',
    },
  ];
}

function getRoleDependencies(role: AgentRole, allAgents: AIAgent[]): AgentDependency[] {
  const deps: AgentDependency[] = [];

  // Upstream
  if (role === 'frontend' || role === 'qa') {
    const backendAgent = allAgents.find((a) => a.role === 'backend');
    deps.push({
      agentId: backendAgent?.id || 'agent_back_default',
      agentName: backendAgent?.name || 'Carlos Backend',
      agentRole: 'backend',
      relationship: 'depends_on',
      description: 'Consumes REST/GraphQL endpoints and data contracts to render UI.',
      protocol: 'HTTP/2 + OpenAPI 3.1',
    });
  }

  if (role === 'devops') {
    const backendAgent = allAgents.find((a) => a.role === 'backend');
    deps.push({
      agentId: backendAgent?.id || 'agent_back_default',
      agentName: backendAgent?.name || 'Carlos Backend',
      agentRole: 'backend',
      relationship: 'collaborates_with',
      description: 'Supervises container images and compiled migration scripts.',
      protocol: 'Docker Registry + GitHub Webhooks',
    });
  }

  // Downstream
  if (role === 'backend') {
    const qaAgent = allAgents.find((a) => a.role === 'qa');
    deps.push({
      agentId: qaAgent?.id || 'agent_qa_default',
      agentName: qaAgent?.name || 'Sara QA',
      agentRole: 'qa',
      relationship: 'feeds_into',
      description: 'Delivers backend artifacts for E2E and load test verification.',
      protocol: 'CI/CD Artifact Store',
    });
    const devopsAgent = allAgents.find((a) => a.role === 'devops');
    deps.push({
      agentId: devopsAgent?.id || 'agent_devops_default',
      agentName: devopsAgent?.name || 'Leo DevOps',
      agentRole: 'devops',
      relationship: 'feeds_into',
      description: 'Provides database configurations and environment variables for production.',
      protocol: 'K8s ConfigMaps + Secrets',
    });
  }

  if (role === 'frontend') {
    const qaAgent = allAgents.find((a) => a.role === 'qa');
    deps.push({
      agentId: qaAgent?.id || 'agent_qa_default',
      agentName: qaAgent?.name || 'Sara QA',
      agentRole: 'qa',
      relationship: 'feeds_into',
      description: 'Delivers frontend builds for visual verification and accessibility audits.',
      protocol: 'Vercel Preview Deployments',
    });
  }

  if (deps.length === 0) {
    deps.push({
      agentId: 'agent_coord',
      agentName: 'Process Orchestrator',
      agentRole: 'manager',
      relationship: 'collaborates_with',
      description: 'Bidirectional synchronization with central task coordinator.',
      protocol: 'EventBus Pub/Sub',
    });
  }

  return deps;
}
